import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CelebrationStats {
  /** 1-indexed ordinal of the just-paid order among this company's paid orders. */
  orderOrdinal: number;
  /** Distinct team members ever celebrated (across delivered + confirmed orders). */
  uniqueTeamMembers: number;
  /** Count of delivered celebrations this calendar year. */
  deliveredThisYear: number;
  /** Count of delivered celebrations the previous calendar year (for delta). */
  deliveredLastYear: number;
  /** Celebrations scheduled in the next 30 days the admin hasn't approved yet. */
  upcomingWaiting: number;
}

/**
 * Pulls the stats used by the Spotify-Wrapped-style post-payment page.
 * Intentionally NOT reused for dashboard, the framing ("you just locked in
 * your Nth celebration") makes no sense outside the success context.
 */
export function useCelebrationStats(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["celebrations", companyId, "stats"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CelebrationStats | null> => {
      if (!companyId) return null;

      const now = new Date();
      const yearStart = new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString();
      const lastYearStart = new Date(Date.UTC(now.getUTCFullYear() - 1, 0, 1)).toISOString();
      const lastYearEnd = yearStart;
      const next30Iso = new Date(now.getTime() + 30 * 86_400_000).toISOString().slice(0, 10);
      const todayIso = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
        .toISOString()
        .slice(0, 10);

      // Fire all queries in parallel. Only one is materialized into a
      // count; the rest fetch just enough rows to derive what we need.
      const [orderOrdinalRes, uniqueRes, yearRes, lastYearRes, upcomingRes] = await Promise.all([
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("payment_status", "paid"),
        // MS68: was selecting just team_member_name and Set-deduping on
        // the string — which collapses two genuinely-different teammates
        // with the same name into one. Now we pull celebration_id too
        // and prefer it for the dedupe key (one celebration = one
        // recipient slot, guaranteed unique). team_member_name remains
        // the fallback for legacy orders that pre-date celebration_id
        // being populated.
        supabase
          .from("orders")
          .select("team_member_name, celebration_id")
          .eq("company_id", companyId)
          // MS341: "ready" replaces "out_for_delivery" to match the
          // actual DB enum the vendor portal writes.
          .in("status", ["confirmed", "baking", "ready", "delivered"]),
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("status", "delivered")
          .gte("delivery_date", yearStart.slice(0, 10)),
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("status", "delivered")
          .gte("delivery_date", lastYearStart.slice(0, 10))
          .lt("delivery_date", lastYearEnd.slice(0, 10)),
        supabase
          .from("celebrations")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("status", "approved")
          .gte("date", todayIso)
          .lte("date", next30Iso),
      ]);

      // MS68: dedupe on celebration_id when present (guaranteed unique
      // per recipient slot). For rows without a celebration_id (legacy
      // pre-link orders), fall back to "name:NAME" as the key so we
      // still count them, just with the original collision risk for
      // those rows only.
      const uniqueKeys = new Set(
        (uniqueRes.data as
          | { team_member_name: string; celebration_id: string | null }[]
          | null
          | undefined)?.map((r) =>
          r.celebration_id ? `cel:${r.celebration_id}` : `name:${r.team_member_name}`,
        ) ?? [],
      );

      return {
        orderOrdinal: orderOrdinalRes.count ?? 0,
        uniqueTeamMembers: uniqueKeys.size,
        deliveredThisYear: yearRes.count ?? 0,
        deliveredLastYear: lastYearRes.count ?? 0,
        upcomingWaiting: upcomingRes.count ?? 0,
      };
    },
  });
}
